import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Bot, Maximize2, Minimize2, ArrowDown, History, Lightbulb, MessageSquarePlus, X } from "lucide-react";
import { API_BASE, api } from "@/lib/api";
import { toast } from "sonner";
import { ChatMessage } from "@/components/chat/ChatMessage";
import { ChatComposer } from "@/components/chat/ChatComposer";
import { ChatRail, DepthSwitch, SuggestionButtons } from "@/components/chat/ChatRail";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const GREETING = { role: "assistant", content: "Hi, I'm EV-IDEX. Ask me about a coin, a scanner opportunity, an indicator or a strategy idea. I ground every number in current EV-IDEX data and tell you plainly when something is unavailable." };

export default function Chat() {
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [depth, setDepth] = useState(() => localStorage.getItem("apx:chat-depth") || "beginner");
  const [full, setFull] = useState(false);
  const [ideasOpen, setIdeasOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [atBottom, setAtBottom] = useState(true);
  const [height, setHeight] = useState("70vh");
  const scrollerRef = useRef(null);
  const wrapRef = useRef(null);
  const pickDepth = (d) => { setDepth(d); localStorage.setItem("apx:chat-depth", d); };

  // Size the chat to the remaining viewport below the header (and above the mobile nav).
  useLayoutEffect(() => {
    const measure = () => {
      if (full || !wrapRef.current) return;
      const top = wrapRef.current.getBoundingClientRect().top + window.scrollY;
      const bottom = window.innerWidth < 768 ? 92 : 12;
      setHeight(`calc(100dvh - ${Math.round(top)}px - ${bottom}px)`);
    };
    measure();
    window.addEventListener("resize", measure);
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(document.body);
    const mo = new MutationObserver(measure);
    if (wrapRef.current?.parentElement) mo.observe(wrapRef.current.parentElement, { childList: true });
    return () => { window.removeEventListener("resize", measure); ro?.disconnect(); mo.disconnect(); };
  }, [full]);

  useEffect(() => {
    if (!full) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") setFull(false); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [full]);

  const scrollToBottom = useCallback((smooth = true) => {
    const el = scrollerRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
  }, []);

  useEffect(() => { if (atBottom) scrollToBottom(false); }, [messages, busy, atBottom, scrollToBottom]);

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 80);
  };

  const loadSessions = useCallback(async () => {
    try { setSessions((await api.get("/chat/sessions")).data); } catch (e) { console.warn("[apex] chat sessions:", e?.message || e); }
  }, []);

  const openSession = useCallback(async (sid) => {
    try {
      const rows = (await api.get(`/chat/${sid}`)).data;
      setSessionId(sid);
      setMessages([GREETING, ...rows.map((r) => ({ role: r.role, content: r.content, ts: r.created_at, id: r.id, followups: r.followups }))]);
      setAtBottom(true);
      setHistoryOpen(false);
    } catch (e) { toast.error("Could not open that conversation"); }
  }, []);

  const newChat = () => { setSessionId(null); setMessages([GREETING]); setInput(""); setHistoryOpen(false); };

  // Restore the most recent conversation so a reload doesn't lose context.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = (await api.get("/chat/sessions")).data;
        if (cancelled) return;
        setSessions(list);
        if (list.length) await openSession(list[0].session_id);
      } catch (e) { console.warn("[apex] chat restore:", e?.message || e); }
    })();
    return () => { cancelled = true; };
  }, [openSession]);

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || busy) return;
    setInput("");
    setIdeasOpen(false);
    setAtBottom(true);
    const now = new Date().toISOString();
    setMessages((m) => [...m, { role: "user", content: text, ts: now }, { role: "assistant", content: "", streaming: true, ts: now }]);
    setBusy(true);
    try {
      const resp = await fetch(`${API_BASE}/chat/stream`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sessionId, depth }),
      });
      if (resp.status === 429) throw new Error("You're sending messages too quickly — please wait a moment.");
      if (resp.status === 402) throw new Error("Daily chat limit reached on your plan.");
      if (!resp.ok || !resp.body) throw new Error("Chat stream failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const payload = JSON.parse(line.slice(5).trim());
            if (payload.type === "delta") {
              setMessages((m) => { const last = m[m.length - 1]; return [...m.slice(0, -1), { ...last, content: (last.content || "") + payload.content }]; });
            } else if (payload.type === "session") {
              setSessionId(payload.session_id);
            } else if (payload.type === "followups") {
              setMessages((m) => { const last = m[m.length - 1]; return [...m.slice(0, -1), { ...last, followups: payload.items }]; });
            } else if (payload.type === "done") {
              setMessages((m) => { const last = m[m.length - 1]; return [...m.slice(0, -1), { ...last, streaming: false }]; });
            } else if (payload.type === "error") {
              throw new Error(payload.error);
            }
          } catch (e) { console.warn("[apex] ignored:", e); }
        }
      }
      loadSessions();
    } catch (e) {
      toast.error("Chat failed: " + (e?.message || ""));
      setMessages((m) => m.filter((x) => !(x.role === "assistant" && x.streaming && !x.content)));
    }
    setBusy(false);
  };

  const shell = full
    ? "fixed inset-0 z-[70] bg-[#070A0F] flex flex-col px-3 md:px-6 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
    : "flex flex-col px-3 md:px-6 pt-2 max-w-[1600px] mx-auto w-full";

  const page = (
    <div ref={wrapRef} data-testid="chat-page" className={shell} style={full ? undefined : { height }}>
      {/* Header row */}
      <div className="flex items-center gap-3 mb-2 shrink-0">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-cyan-500/15 border border-cyan-900/60 shrink-0">
          <Bot className="w-5 h-5 text-cyan-300" />
        </div>
        <div className="min-w-0 hidden sm:block">
          <h1 className="font-display text-lg md:text-xl font-bold leading-tight truncate">AI Research Assistant</h1>
          <div className="text-[11px] md:text-xs text-slate-400 truncate">GPT-6 Sol · grounded in live EV-IDEX data · educational only</div>
        </div>
        <div className="ml-auto flex items-center gap-2 shrink-0">
          <div className="lg:hidden"><DepthSwitch depth={depth} onPick={pickDepth} compact /></div>
          <button type="button" data-testid="chat-history-btn" onClick={() => setHistoryOpen(true)} className="lg:hidden btn-ghost rounded-xl w-10 h-10 flex items-center justify-center" aria-label="Conversation history"><History className="w-4 h-4" /></button>
          <button type="button" data-testid="chat-fullscreen-toggle" onClick={() => setFull((f) => !f)} className="btn-ghost rounded-xl h-10 px-3 flex items-center gap-2 text-sm" aria-label={full ? "Exit full screen" : "Full screen chat"}>
            {full ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            <span className="hidden md:inline">{full ? "Exit full screen" : "Full screen"}</span>
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex gap-5">
        {/* Conversation column */}
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="relative flex-1 min-h-0">
            <div ref={scrollerRef} onScroll={onScroll} data-testid="chat-scroller" className="h-full overflow-y-auto apx-card p-3 md:p-6 lg:p-8 space-y-6 md:space-y-8 scroll-smooth">
              {messages.map((m, i) => <ChatMessage key={m.id || `${m.role}-${m.ts || i}-${i}`} m={m} index={i} onFollowUp={(q) => send(q)} busy={busy} />)}
              <div className="h-2" />
            </div>
            {!atBottom && (
              <button type="button" data-testid="chat-scroll-latest" onClick={() => { setAtBottom(true); scrollToBottom(); }} className="absolute bottom-4 left-1/2 -translate-x-1/2 btn-primary rounded-full px-4 py-2 text-sm flex items-center gap-2 shadow-lg">
                <ArrowDown className="w-4 h-4" /> Latest
              </button>
            )}
          </div>

          {/* Mobile / tablet ideas strip */}
          <div className="lg:hidden mt-2 shrink-0">
            <div className="flex items-center gap-2 mb-2">
              <button type="button" data-testid="chat-ideas-toggle" onClick={() => setIdeasOpen((o) => !o)} className={`rounded-full px-3.5 py-2 text-sm flex items-center gap-2 border transition-colors ${ideasOpen ? "border-cyan-700/60 bg-cyan-500/[0.1] text-cyan-100" : "border-slate-800 text-slate-300"}`}>
                <Lightbulb className="w-4 h-4" /> {ideasOpen ? "Hide ideas" : "Question ideas"}
              </button>
              <button type="button" data-testid="chat-new-mobile" onClick={newChat} className="rounded-full px-3.5 py-2 text-sm flex items-center gap-2 border border-slate-800 text-slate-300"><MessageSquarePlus className="w-4 h-4" /> New</button>
            </div>
            {ideasOpen && <SuggestionButtons onPick={(q) => send(q)} busy={busy} layout="strip" />}
          </div>

          <div className="mt-2 shrink-0">
            <ChatComposer value={input} onChange={setInput} onSend={() => send()} busy={busy} large={full} />
            <div className="text-[11px] text-slate-500 text-center mt-1 hidden md:block">Enter to send · Shift+Enter for a new line · Not financial advice — always verify with your own research.</div>
          </div>
        </div>

        <ChatRail depth={depth} onDepth={pickDepth} onSuggest={(q) => send(q)} busy={busy} sessions={sessions} activeSession={sessionId} onOpenSession={openSession} onNewChat={newChat} />
      </div>

      <Sheet open={historyOpen} onOpenChange={setHistoryOpen}>
        <SheetContent side="bottom" className="bg-[#0F141C] border-slate-800 text-slate-100 max-h-[80dvh] overflow-y-auto rounded-t-2xl" data-testid="chat-history-sheet">
          <SheetHeader><SheetTitle className="font-display flex items-center gap-2"><History className="w-4 h-4" /> Conversations</SheetTitle></SheetHeader>
          <button type="button" onClick={newChat} className="btn-primary w-full rounded-xl px-4 py-3 text-sm font-medium flex items-center gap-2 justify-center mt-3"><MessageSquarePlus className="w-4 h-4" /> New conversation</button>
          <div className="mt-3 space-y-2">
            {sessions.map((s) => (
              <button key={s.session_id} type="button" onClick={() => openSession(s.session_id)} className={`w-full text-left rounded-xl px-4 py-3 border ${s.session_id === sessionId ? "border-cyan-700/60 bg-cyan-500/[0.08]" : "border-slate-800"}`}>
                <div className="truncate text-sm">{s.title || s.last_message}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{s.count} messages</div>
              </button>
            ))}
            {!sessions.length && <div className="text-sm text-slate-500 flex items-center gap-2"><X className="w-4 h-4" /> No saved conversations yet.</div>}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );

  return full ? createPortal(page, document.body) : page;
}
